import * as React from "react";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";

import MultilineText from "@hl/base-components/lib/MultilineText";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Flex, ActionIcon, Center, Stack } from "@mantine/core";
import { IconArrowNarrowLeft } from "@tabler/icons";

import { getDetailPageUrl } from "~config";
import { useLinkStyles } from "~features/MintPage/components/InternalLink";
import { InternalLinkProxy } from "~features/MintPage/components/InternalLinkProxy";
import TokenDetailsCardSection from "~features/token-details/TokenDetailsCardSection";
import useMintState from "~hooks/useMintState";
import useMintTokensState from "~hooks/useMintTokensState";

type Props = {
  selectedTokenIndex: number;
  setSelectedTokenIndex: Dispatch<SetStateAction<number>>;
};

const TokenMarketplaceActionsMobile = ({
  selectedTokenIndex,
  setSelectedTokenIndex,
}: Props) => {
  const { collection, isCollectorChoiceMint } = useMintState();
  const { classes: classesLink } = useLinkStyles();
  const { tokens, loadMore } = useMintTokensState();
  const token = tokens?.[selectedTokenIndex];
  const { isMedium } = useMantineMediaQueries({ withInitialValues: true });

  useEffect(() => {
    if (selectedTokenIndex === (tokens?.length ?? 0) - 2) {
      loadMore();
    }
  }, [selectedTokenIndex]);

  const moveToToken = useCallback(
    (moveFor: number) => {
      const newIndex = selectedTokenIndex + moveFor;
      if (newIndex < 0 || !tokens) {
        return;
      }
      setSelectedTokenIndex((index) => index + moveFor);
    },
    [selectedTokenIndex, tokens]
  );

  if (!collection) return null;

  return (
    <Stack
      spacing="sm"
      pt={12}
      pos={!isMedium ? "static" : "absolute"}
      bottom={16}
    >
      <Flex w={isMedium ? 440 : "100%"} justify="space-between" align="center">
        <ActionIcon
          onClick={() => moveToToken(-1)}
          size={48}
          disabled={selectedTokenIndex === 0}
        >
          <IconArrowNarrowLeft />
        </ActionIcon>
        <InternalLinkProxy
          className={classesLink.grow}
          to={
            collection ? getDetailPageUrl(collection, token?.tokenId ?? "") : ""
          }
          isCollectorsChoice={isCollectorChoiceMint}
          minted={token?.minted ?? false}
        >
          <MultilineText size="xl" weight={WEIGHT_BOLD} numLines="2">
            {token?.name}
          </MultilineText>
        </InternalLinkProxy>
        <ActionIcon
          onClick={() => moveToToken(1)}
          size={48}
          disabled={selectedTokenIndex === (tokens?.length ?? 1) - 1}
        >
          <IconArrowNarrowLeft style={{ transform: "rotate(180deg)" }} />
        </ActionIcon>
      </Flex>
      {token && (
        <Center w="100%" maw={400}>
          <TokenDetailsCardSection token={token} compact />
        </Center>
      )}
    </Stack>
  );
};

export default TokenMarketplaceActionsMobile;
